import { Injectable } from '@angular/core'
import { ModalPositionService } from '@helios/user-preferences'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import { CashEntryComponent } from '../../components/single-transaction/cash-entry/cash-entry.component'
import { DirectTransferComponent } from '../../components/single-transaction/direct-transfer/direct-transfer.component'
import { FxForwardModalComponent } from '../../components/single-transaction/fx-forward/fx-forward.component'
import { FxSpotTradeModalComponent } from '../../components/single-transaction/fx-spot-trade/fx-spot-trade.component'
import { SecurityTransferModalComponent } from '../../components/single-transaction/security-transfer/security-transfer.component'
import { TradeEntryModalComponent } from '../../components/single-transaction/trade-entry/trade-entry.component'
import { WireTransferComponent } from '../../components/single-transaction/wire-transfer/wire-transfer.component'
import { CashEntryInputModel } from '../../model/single-transaction/cash-entry/cash-entry.model'
import {
  TransactionModalInput,
  TransactionModalMode,
} from '../../model/single-transaction/popup-input.model'
import { TranCategory } from '../../model/transaction-category.model'
import {
  getTransactionCategoryFromTypeKey,
  getTransactionTypeKeyFromCode,
} from '../../util/common-utils'
import { CreateEditTransactionService } from '../create-edit-transaction.service'

/** Service for showing and hiding the advanced filters modal. */
@Injectable({
  providedIn: 'root',
})
export class CreateTransactionsModalService {
  constructor(
    private readonly modalService: NgbModal,
    readonly createEditTransactionService: CreateEditTransactionService,
    readonly modalPositionService: ModalPositionService,
  ) {}

  /**
   * required to mediate calls to respective functions based on transTypeCode
   *
   * @param transTypeCode transaction type code
   * @param transInput transaction input
   */
  toggleCreateTransactionModal(
    transTypeCode: string,
    transInput: TransactionModalInput,
  ) {
    const transType = getTransactionTypeKeyFromCode(transTypeCode)
    const tranCategory = getTransactionCategoryFromTypeKey(transType)
    switch (tranCategory) {
      case TranCategory.TRADE:
        this.toggleTradeEntryModal(transInput)
        return
      case TranCategory.TRANSFER:
        if ('TRANSFER_OUT' == transType && transInput.transactionId) {
          if (transInput.modalMode === TransactionModalMode.CreateFromExtTran) {
            this.toggleSecurityTransferModal(transInput)
          } else {
            this.createEditTransactionService
              .fetchTranLinkInfo(transInput.transactionId)
              .subscribe(tranLink => {
                if (tranLink) {
                  transInput.tranLinkInfo = tranLink
                  this.toggleDirectTransferModal(transInput)
                } else {
                  this.toggleSecurityTransferModal(transInput)
                }
              })
          }
          return
        }
        this.toggleSecurityTransferModal(transInput)
        return
      case TranCategory.CASH_ENTRY:
        this.toggleCashEntryModal(transInput)
        return
      case TranCategory.FX_TRADE:
        this.toggleFxSpotTradeModal(transInput)
        return
      case TranCategory.CURRENCY_FORWARD:
        this.toggleFxForwardModal(transInput)
        return
      case TranCategory.DIRECT_TRANSFER:
        this.toggleWireTransferModal(transInput)
        return
      case TranCategory.OTHER:
        return
    }
  }

  /**
   * fx type modal opener
   *
   * @param transInput transaction input
   */
  toggleFxSpotTradeModal(transInput: TransactionModalInput) {
    const compKey = 'fx-spot-trade'
    const uniqueId = compKey + '-' + Date.now()
    const modalRef = this.modalService.open(FxSpotTradeModalComponent, {
      size: 'lg',
      windowClass: `modal-window-click-through ${uniqueId}`,
      scrollable: true,
      keyboard: false,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          compKey,
        )
      },
    } as NgbModalOptions)
    modalRef.componentInstance.transInput = transInput
    this.modalPositionService.handleModalPositionSetup(uniqueId, compKey)
  }

  /**
   * cash type modal opener
   *
   * @param transInput transaction input
   * @param prePopulatedInput cash entry input model
   */
  toggleCashEntryModal(
    transInput: TransactionModalInput,
    prePopulatedInput?: CashEntryInputModel,
  ) {
    const compKey = 'cash-entry'
    const uniqueId = compKey + '-' + Date.now()
    const modalRef = this.modalService.open(CashEntryComponent, {
      size: 'xl',
      windowClass: `modal-window-click-through ${uniqueId}`,
      keyboard: false,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          compKey,
        )
      },
    } as NgbModalOptions)
    modalRef.componentInstance.transInput = transInput
    modalRef.componentInstance.prePopulatedInput = prePopulatedInput
    this.modalPositionService.handleModalPositionSetup(uniqueId, compKey)
  }

  /**
   * trade type modal opener
   *
   * @param transInput transaction input
   */
  toggleTradeEntryModal(transInput: TransactionModalInput) {
    const compKey = 'trade-entry'
    const uniqueId = compKey + '-' + Date.now()
    const modalRef = this.modalService.open(TradeEntryModalComponent, {
      size: 'xl',
      windowClass: `modal-window-click-through ${uniqueId}`,
      keyboard: false,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          compKey,
        )
      },
    } as NgbModalOptions)
    modalRef.componentInstance.transInput = transInput
    this.modalPositionService.handleModalPositionSetup(uniqueId, compKey)
  }

  /**
   * forward type modal opener
   *
   * @param transInput transaction input
   */
  toggleFxForwardModal(transInput: TransactionModalInput) {
    const compKey = 'fx-forward'
    const uniqueId = compKey + '-' + Date.now()
    const modalRef = this.modalService.open(FxForwardModalComponent, {
      size: 'lg',
      windowClass: `modal-window-click-through ${uniqueId}`,
      scrollable: true,
      keyboard: false,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          compKey,
        )
      },
    } as NgbModalOptions)
    modalRef.componentInstance.transInput = transInput
    this.modalPositionService.handleModalPositionSetup(uniqueId, compKey)
  }

  /**
   * fx type modal opener
   *
   * @param transInput transaction input
   */
  toggleDirectTransferModal(transInput: TransactionModalInput) {
    const compKey = 'direct-transfer'
    const uniqueId = compKey + '-' + Date.now()
    const modalRef = this.modalService.open(DirectTransferComponent, {
      size: 'lg',
      windowClass: `modal-window-click-through ${uniqueId}`,
      scrollable: true,
      keyboard: false,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          compKey,
        )
      },
    } as NgbModalOptions)
    modalRef.componentInstance.transInput = transInput
    this.modalPositionService.handleModalPositionSetup(uniqueId, compKey)
  }

  /**
   * Direct Cash Transfer modal opener
   *
   * @param transInput transaction input
   */
  toggleWireTransferModal(transInput: TransactionModalInput) {
    const compKey = 'wire-transfer'
    const uniqueId = compKey + '-' + Date.now()
    const modalRef = this.modalService.open(WireTransferComponent, {
      size: 'lg',
      windowClass: `modal-window-click-through ${uniqueId}`,
      scrollable: true,
      keyboard: false,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          compKey,
        )
      },
    } as NgbModalOptions)
    modalRef.componentInstance.transInput = transInput
    this.modalPositionService.handleModalPositionSetup(uniqueId, compKey)
  }

  /**
   * security type modal opener
   *
   * @param transInput transaction input
   */
  toggleSecurityTransferModal(transInput: TransactionModalInput) {
    const compKey = 'security-transfer'
    const uniqueId = compKey + '-' + Date.now()
    const modalRef = this.modalService.open(SecurityTransferModalComponent, {
      size: 'xl',
      windowClass: `modal-window-click-through ${uniqueId}`,
      scrollable: true,
      keyboard: false,
      beforeDismiss: () => {
        return this.modalPositionService.handleModalPositionSaveOnDismiss(
          uniqueId,
          compKey,
        )
      },
    } as NgbModalOptions)
    modalRef.componentInstance.transInput = transInput
    this.modalPositionService.handleModalPositionSetup(uniqueId, compKey)
  }
}
